import React, { useMemo, useRef, useState } from 'react'
import {
  Button,
  SelectInput,
  TextInput,
  ToggleInput,
  RadioInput,
  FeaturePromptPopup,
  DualSelect,
  Info,
} from '@demandlocal/components'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { DateRange } from '../../lib/types/DateRange'
import dayjs from 'dayjs'
import { ReactComponent as IconRemove } from './../../images/icons/remove.svg'
import { isEmpty, take } from 'lodash'
import { useGetAllCampaignsByDealership } from '../../api/dealership/campaigns/useGetAllCampaignsByDealership'
import { HtmlPortalNode } from 'react-reverse-portal'
import { autoPlacement, FloatingArrow, offset, useFloating } from '@floating-ui/react'
import { ReactComponent as SuccessIcon } from '../../images/icons/check_fill.svg'
import * as portals from 'react-reverse-portal'
import { AnimatePresence, motion } from 'framer-motion'
import classNames from 'classnames'
import { arrow, autoUpdate, shift } from '@floating-ui/react-dom'
import { usePostCampaignQuickReportGenerator } from '../../api/dealership/campaigns/usePostCampaignQuickReportGenerator'
import { useSelector } from 'react-redux'
import { currentUserSelector } from '../../store/selectors/userSelector'
import { match } from 'ts-pattern'
import { toast } from 'react-hot-toast'
import { useGetTagCodes } from '../../api/utils/useGetTagCodes'
import { Option } from '../../lib/types/Option'
import { useDealership } from '../../store/zustand/dealershipStore'
import useGetServiceEmailAddress from '../../api/dealership/utils/useGetServiceEmailAddress'
import useCheckSpreadsheetAccess from '../../api/dealership/utils/useCheckSpreadsheetAccess'

const DEFAULT_METRICS_COUNT = 4

export const METRICS = [
  { label: 'Booking Month', value: 'booking_month' },
  { label: 'Agency', value: 'agency' },
  { label: 'Client', value: 'client' },
  { label: 'Campaign', value: 'campaign' },
  { label: 'Pacing ID', value: 'pacing_id' },
  { label: 'Product Code', value: 'product_code' },
  { label: 'Start Date', value: 'start_date' },
  { label: 'End Date', value: 'end_date' },
  { label: 'Impressions', value: 'impressions' },
  { label: 'Clicks', value: 'clicks' },
  { label: 'CTR', value: 'ctr' },
  { label: 'RSVs', value: 'rsvs' },
  { label: 'Budget', value: 'budget' },
  { label: 'Booked Impressions', value: 'book_impressions' },
  { label: 'Percent Delivered', value: 'percent_delivered' },
  { label: 'Views', value: 'views' },
  { label: '25% Completion', value: '25_completion' },
  { label: '50% Completion', value: '50_completion' },
  { label: '75% Completion', value: '75_completion' },
  { label: 'Viewability', value: 'viewability' },
  { label: 'Phone Calls', value: 'phone_calls' },
  { label: 'Impression Share', value: 'impression_share' },
  { label: 'Click Share', value: 'click_share' },
  { label: 'Impression Share Lost to Rank', value: 'impression_share_lost_to_rank' },
  { label: 'Impression Share Lost to Budget', value: 'impression_share_lost_to_budget' },
] as const

type FormValues = {
  email: string
  campaigns: Array<{
    client_campaign_id: string
    campaign_name: string
    product_name: string
    product_code: string
  }>
  all_campaigns: boolean
  metrics_all: boolean
  conversions_all: boolean
  recurring: boolean
  schedule_frequency: string | null
  schedule_day: string | null
  reportFormat: string
  sheet_url: string
  period: { label: string; value: string }
} & Record<(typeof METRICS)[number]['value'], boolean>

export default function CampaignBreakdownReportForm({
  portalNode,
}: {
  portalNode: HtmlPortalNode
}): JSX.Element {
  const { mutateAsync } = usePostCampaignQuickReportGenerator()
  const [expanded, setExpanded] = useState(false)
  const user = useSelector(currentUserSelector)
  const { data: conversions } = useGetTagCodes({ pageSize: 100 })

  const conversionsOptions = useMemo(() => {
    return conversions?.data?.map((d) => ({ label: d.tag_name, value: d.tag_name }))
  }, [conversions?.data])
  const formContext = useForm<FormValues>({
    defaultValues: {
      email: user?.email || '',
      campaigns: [],
      all_campaigns: false,
      metrics_all: false,
      conversions_all: false,
      recurring: false,
      schedule_frequency: undefined,
    },
  })
  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: dayjs().startOf('month').subtract(3, 'month').format('YYYY-MM-DD'),
      endDate: dayjs()
        .startOf('month')
        .subtract(1, 'month')
        .endOf('month')
        .format('YYYY-MM-DD'),
    },
  })

  const {
    data: campaigns,
    isLoading: isLoadingCampaigns,
    isPreviousData,
  } = useGetAllCampaignsByDealership({
    filters: {
      ...filters.dateRange,
      onlyFlights: 1,
      includeAdditionalMetrics: 0,
    },
  })
  const uniqueCampaigns = useMemo(() => {
    const groupedMap = new Map()
    campaigns?.data?.forEach((obj) => {
      const key = obj.client_campaign_id
      if (!groupedMap.has(key)) {
        groupedMap.set(key, {
          client_campaign_id: key,
          campaign_name: obj.client_campaign_display_name,
          product_name: obj.product_name,
          product_code: obj.product_code,
          label: obj.client_campaign_display_name,
          value: key,
        })
      }
    })
    return Array.from(groupedMap.values())
  }, [campaigns])

  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { isSubmitting, isSubmitSuccessful },
  } = formContext
  const selectedCampaigns = watch('campaigns')
  const allCampaigns = watch('all_campaigns')

  const recurring = watch('recurring')
  const scheduleFrequency = watch('schedule_frequency')
  const period = watch('period')
  const reportFormat = watch('reportFormat')
  const sheetURL = watch('sheet_url')

  const { data: serviceEmail } = useGetServiceEmailAddress()
  const { mutateAsync: checkAccess } = useCheckSpreadsheetAccess()

  const checkAllMetricsToggled = (): boolean => {
    return getValues(METRICS.map((metric) => metric.value)).every(
      (value) => value === true,
    )
  }
  const getDatesFromPeriod = (
    period:
      | 'last_month'
      | 'last_3_months'
      | 'last_6_months'
      | 'last_year'
      | 'year_to_date',
  ): { start: string; end: string } => {
    return match(period)
      .with('last_month', () => {
        return {
          start: dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
          end: dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
        }
      })
      .with('last_3_months', () => {
        return {
          start: dayjs().subtract(3, 'month').startOf('month').format('YYYY-MM-DD'),
          end: dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
        }
      })
      .with('last_6_months', () => {
        return {
          start: dayjs().subtract(6, 'month').startOf('month').format('YYYY-MM-DD'),
          end: dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
        }
      })
      .with('last_year', () => {
        return {
          start: dayjs().subtract(1, 'year').startOf('month').format('YYYY-MM-DD'),
          end: dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
        }
      })
      .with('year_to_date', () => {
        return {
          start: dayjs().startOf('year').format('YYYY-MM-DD'),
          end: dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
        }
      })
      .exhaustive()
  }

  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    if (values?.reportFormat === 'google_sheet') {
      const googleAccessToast = toast.loading('Checking access...')
      if (!serviceEmail?.email) {
        toast.error('Access denied!', { id: googleAccessToast })
        return
      }

      await checkAccess(
        {
          spreadsheet_url: sheetURL,
          service_email: serviceEmail?.email,
        },
        {
          onSuccess: () => {
            toast.success('Access granted!', { id: googleAccessToast })
          },
          onError: () => {
            toast.error('Access denied!', { id: googleAccessToast })
          },
        },
      )
    }
    const toastId = toast.loading('Generating report...')
    await mutateAsync({
      ...values,
      metrics: METRICS.map((metric) => metric.value).filter(
        (metric) => values[metric] === true,
      ),
      conversions: conversionsOptions
        ?.map((conversion) => conversion.value)
        .filter((conversion) => values[conversion] === true),
      reportFormat: values.reportFormat,
      sheet_url: values?.sheet_url,
      period: values.period.value,
      recurring: values.recurring,
      recurring_interval: values.recurring
        ? {
            type: values.schedule_frequency,
            day: values.schedule_day.value,
          }
        : undefined,
      scheduleDay: values.scheduleDay?.value,
    })
    toast.success('Report sent', { id: toastId })
  }

  const referenceRef = useRef<HTMLButtonElement | null>(null)

  const getDayOrdinal = (day: number): 'th' | 'st' | 'nd' | 'rd' => {
    if (day > 3 && day < 21) return 'th'
    switch (day % 10) {
      case 1:
        return 'st'
      case 2:
        return 'nd'
      case 3:
        return 'rd'
      default:
        return 'th'
    }
  }

  return (
    <>
      <portals.InPortal node={portalNode}>
        <Button
          ref={referenceRef}
          animate={true}
          type={'button'}
          variant={'tertiary'}
          customStyles={classNames('print:hidden', {
            '!bg-primary-persian-blue/5 !text-primary-persian-blue !border-primary-persian-blue/10':
              expanded,
          })}
          onClick={(e) => {
            e.preventDefault()
            setExpanded((prev) => !prev)
          }}>
          Generate Custom Report
        </Button>
      </portals.InPortal>
      <div className="print:hidden">
        <FeaturePromptPopup
          title={'Quick report generator'}
          description={
            <p>
              Pick the metrics you need,
              <br /> campaigns and voila! report DONE!
            </p>
          }
          referenceRef={referenceRef}
          name={'quick-report-generator-button'}
        />
      </div>
      <FormProvider {...formContext}>
        <AnimatePresence>
          {expanded && (
            <motion.form
              animate={'open'}
              initial={'collapsed'}
              exit={'collapsed'}
              transition={{ type: 'spring', mass: 1, damping: 50, stiffness: 500 }}
              variants={{
                open: {
                  opacity: 1,
                  height: 'auto',
                },
                collapsed: {
                  opacity: 0,
                  height: 0,
                },
              }}
              onSubmit={handleSubmit(onSubmit)}>
              <div
                className={
                  'p-5 relative rounded-md border-2 bg-primary-persian-blue/5 border-primary-persian-blue/10 mb-8'
                }>
                {isSubmitSuccessful && (
                  <div
                    className={
                      'h-96 w-full mx-auto flex flex-col items-center justify-center'
                    }>
                    <SuccessIcon />
                    <div className={'pt-5 font-bold text-[28px]'}>
                      Great! Your report is now being generated.
                    </div>
                    <div className={'pt-3 font-medium text-base'}>
                      You will receive an email with the report shortly.
                    </div>
                    <div className={'pt-4 flex flex-row gap-2'}>
                      <Button
                        variant={'tertiary'}
                        type={'button'}
                        onClick={(e) => {
                          e.preventDefault()
                          setExpanded(false)
                          reset()
                        }}>
                        Close
                      </Button>
                      <Button
                        variant={'rainbow'}
                        onClick={(e) => {
                          e.preventDefault()
                          reset()
                        }}>
                        Generate another report
                      </Button>
                    </div>
                  </div>
                )}
                {isSubmitting && (
                  <div
                    className={'w-full h-full absolute top-0 left-0 bg-white/50 z-20'}
                  />
                )}
                {!isSubmitSuccessful && (
                  <div>
                    <div
                      className={
                        'flex flex-row justify-between items-center border-b-2 pb-5 border-primary-persian-blue/10'
                      }>
                      <h3 className={'font-bold text-primary-xiketic text-lg'}>
                        Quick report generator
                      </h3>
                      <div className={'flex items-center gap-4'}>
                        <p className={'text-davy-gray text-sm font-medium'}>Period</p>
                        <SelectInput
                          required={true}
                          name={'period'}
                          onChange={(value) => {
                            const { start, end } = getDatesFromPeriod(value.value)
                            setValue('all_campaigns', false)
                            setValue('campaigns', [])
                            setFilters({
                              dateRange: {
                                startDate: start,
                                endDate: end,
                              },
                            })
                          }}
                          options={[
                            {
                              label: 'Last month',
                              value: 'last_month',
                            },
                            {
                              label: 'Last 3 months',
                              value: 'last_3_months',
                            },
                            {
                              label: 'Last 6 months',
                              value: 'last_6_months',
                            },
                            {
                              label: dayjs().subtract(1, 'year').format('YYYY'),
                              value: 'last_year',
                            },
                            {
                              label: 'Year to date',
                              value: 'year_to_date',
                            },
                          ]}
                        />
                      </div>
                    </div>
                    <div
                      className={
                        'flex flex-col gap-3 py-5 border-b-2 border-primary-persian-blue/10'
                      }>
                      <div className={'flex flex-row justify-between'}>
                        <h4 className={'font-bold text-base text-primary-xiketic'}>
                          Choose campaigns
                        </h4>
                        <div className={'flex flex-row gap-2 items-center '}>
                          <ToggleInput
                            disabled={
                              isPreviousData || isLoadingCampaigns || !period?.value
                            }
                            trackClassName={'bg-primary-persian-blue/10'}
                            name={'all_campaigns'}
                            label={'All'}
                            onChange={(value) => {
                              if (value) setValue('campaigns', uniqueCampaigns)
                              else setValue('campaigns', [])
                            }}
                          />
                        </div>
                      </div>
                      <div className={'flex flex-row items-center gap-3 flex-wrap'}>
                        <DualSelect
                          disabled={
                            allCampaigns ||
                            isPreviousData ||
                            isLoadingCampaigns ||
                            !period?.value
                          }
                          options={uniqueCampaigns}
                          name={'campaigns'}
                          buttonTitle={'Choose campaigns'}
                          optionsListTitle={'Campaigns'}
                          selectedOptionsListTitle={'Selected Campaigns'}
                        />
                        {selectedCampaigns?.map((campaign) => (
                          <div
                            className={
                              'flex flex-row p-2 items-center bg-white rounded-md'
                            }
                            key={campaign.client_campaign_id}>
                            <div className={'flex flex-col'}>
                              <h4 className={'font-bold text-sm text-primary-xiketic'}>
                                {campaign.campaign_name}
                              </h4>
                              <span className={'font-medium text-web-gray text-sm'}>
                                {campaign.product_name}
                              </span>
                            </div>
                            <div className={'flex w-24 justify-end'}>
                              <IconRemove
                                className={'cursor-pointer'}
                                onClick={(e) => {
                                  e.preventDefault()
                                  setValue('all_campaigns', false)
                                  setValue(
                                    'campaigns',
                                    selectedCampaigns.filter(
                                      (c) =>
                                        c.client_campaign_id !==
                                        campaign.client_campaign_id,
                                    ),
                                  )
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div
                      className={
                        'flex flex-col gap-3 py-5 border-b-2 border-primary-persian-blue/10'
                      }>
                      <div className={'flex flex-row justify-between'}>
                        <h4 className={'font-bold text-base text-primary-xiketic'}>
                          What metrics do you want to generate?
                        </h4>
                        <div className={'flex flex-row gap-2 items-center '}>
                          <ToggleInput
                            disabled={isEmpty(selectedCampaigns)}
                            trackClassName={'bg-primary-persian-blue/10'}
                            name={'metrics_all'}
                            label={'All'}
                            onChange={(value) => {
                              if (value)
                                METRICS.forEach((metric) => setValue(metric.value, true))
                              else
                                METRICS.forEach((metric) => setValue(metric.value, false))
                            }}
                          />
                        </div>
                      </div>
                      <div className={'flex flex-row gap-3 items-center relative'}>
                        {take(METRICS, DEFAULT_METRICS_COUNT).map((metric) => (
                          <div
                            key={metric.value}
                            className={
                              'flex flex-row gap-3 items-center border-2 p-3 rounded-lg border-primary-persian-blue/10 has-[:checked]:bg-primary-persian-blue/20 bg-white'
                            }>
                            <ToggleInput
                              disabled={isEmpty(selectedCampaigns)}
                              trackClassName={'bg-primary-persian-blue/10'}
                              label={metric.label}
                              onChange={(value) => {
                                if (value && checkAllMetricsToggled())
                                  setValue('metrics_all', true)
                                else setValue('metrics_all', false)
                              }}
                              name={metric.value}
                            />
                          </div>
                        ))}
                        <MetricsPopover
                          metrics={METRICS}
                          conversions={conversionsOptions}
                          disabledInputs={isEmpty(selectedCampaigns)}
                        />
                      </div>
                    </div>
                    <div className={'flex flex-col pt-5 gap-4'}>
                      <div className={'flex flex-row justify-between items-center'}>
                        <h2 className={'font-bold text-primary-xiketic'}>Send it to</h2>
                        <div>
                          <ToggleInput
                            name={'recurring'}
                            label={'Set as recurring report'}
                          />
                        </div>
                      </div>
                      <div>
                        <TextInput
                          rules={{
                            required: 'Email is required',
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                              message: 'Invalid email address',
                            },
                          }}
                          name={'email'}
                          label={'Email'}
                        />
                      </div>
                      <div className={'grid grid-cols-3 gap-2 flex-row'}>
                        <RadioInput
                          rules={{
                            required: 'Report format is required',
                          }}
                          label={'Report Format'}
                          options={[
                            {
                              label: 'Excel (XLSX)',
                              value: 'xlsx',
                            },
                            {
                              label: 'Zipped CSV',
                              value: 'csv',
                            },
                            {
                              label: 'Google sheet',
                              value: 'google_sheet',
                            },
                          ]}
                          name={'reportFormat'}
                        />
                        {recurring && (
                          <>
                            <RadioInput
                              onChange={() => {
                                setValue('schedule_day', null)
                              }}
                              label={'Every'}
                              options={[
                                {
                                  label: 'Day',
                                  value: 'weekly',
                                },
                                {
                                  label: 'Month',
                                  value: 'monthly',
                                },
                              ]}
                              name={'schedule_frequency'}
                            />
                            {scheduleFrequency && (
                              <SelectInput
                                label={
                                  scheduleFrequency === 'weekly'
                                    ? 'Which day?'
                                    : 'Which day of the month?'
                                }
                                name={'schedule_day'}
                                options={
                                  scheduleFrequency === 'weekly'
                                    ? [
                                        { label: 'Monday', value: 1 },
                                        { label: 'Tuesday', value: 2 },
                                        { label: 'Wednesday', value: 3 },
                                        { label: 'Thursday', value: 4 },
                                        { label: 'Friday', value: 5 },
                                        { label: 'Saturday', value: 6 },
                                        { label: 'Sunday', value: 7 },
                                      ]
                                    : Array.from({ length: 31 }, (_, i) => ({
                                        label: `${i + 1}${getDayOrdinal(i + 1)}`,
                                        value: i + 1,
                                      }))
                                }
                              />
                            )}
                          </>
                        )}
                      </div>
                      {reportFormat === 'google_sheet' && (
                        <div className="flex flex-col gap-2">
                          <TextInput
                            name={'sheet_url'}
                            label={'Google sheet URL'}
                            rules={{ required: true }}
                          />
                          <h3 className="text-sm">
                            Share your Google sheet with this email address:{' '}
                            <strong>{serviceEmail?.email}</strong>
                          </h3>
                          <div className="w-fit">
                            <Button
                              variant="secondary"
                              disabled={!sheetURL}
                              onClick={async (e) => {
                                e.preventDefault()
                                const toastId = toast.loading('Checking access...')
                                if (!serviceEmail?.email) {
                                  toast.error('Access denied!', { id: toastId })
                                  return
                                }

                                await checkAccess(
                                  {
                                    spreadsheet_url: sheetURL,
                                    service_email: serviceEmail?.email,
                                  },
                                  {
                                    onSuccess: () => {
                                      toast.success('Access granted!', { id: toastId })
                                    },
                                    onError: () => {
                                      toast.error('Access denied!', { id: toastId })
                                    },
                                  },
                                )
                              }}>
                              Verify access
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className={'flex flex-col gap-3 pt-5 items-end'}>
                      <div>
                        <Button
                          disabled={isSubmitting}
                          animate={false}
                          variant={isSubmitSuccessful ? 'success' : 'rainbow'}
                          type={'submit'}>
                          {isSubmitSuccessful
                            ? 'Report sent'
                            : isSubmitting
                            ? 'Generating report...'
                            : 'Generate report'}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.form>
          )}
        </AnimatePresence>
      </FormProvider>
    </>
  )
}

const MetricsPopover = ({
  metrics,
  conversions,
  disabledInputs,
}: {
  metrics: typeof METRICS
  conversions?: Array<Option>
  disabledInputs
}): JSX.Element => {
  const dealership = useDealership()
  const [isOpen, setIsOpen] = useState(false)
  const { setValue, getValues } = useForm()
  const checkAllMetricsToggled = (): boolean => {
    return getValues(metrics.map((metric) => metric.value)).every(
      (value) => value === true,
    )
  }
  const arrowRef = useRef(null)

  const { refs, context } = useFloating({
    placement: 'bottom',
    middleware: [
      autoPlacement({
        allowedPlacements: ['bottom'],
      }),
      shift(),
      offset(24),
      arrow({ element: arrowRef, padding: 16 }),
    ],
    whileElementsMounted: autoUpdate,
  })

  return (
    <div>
      <div
        ref={refs.setReference}
        className={'font-bold text-primary-persian-blue text-sm cursor-pointer'}
        onClick={(e) => {
          setIsOpen((prev) => !prev)
        }}>
        + {metrics.length - DEFAULT_METRICS_COUNT} more
      </div>
      {isOpen && (
        <div
          ref={refs.setFloating}
          className={
            'absolute bg-white p-5 rounded-md top-16 z-10 shadow-2xl drop-shadow-2xl left-0 top-0'
          }>
          <FloatingArrow
            width={16}
            height={8}
            staticOffset={(refs.floating?.current?.offsetWidth ?? 0) / 2 - 12}
            ref={arrowRef}
            context={context}
            className={'fill-white'}
          />
          <div className={'flex justify-between items-center'}>
            <h4 className={'font-bold text-base text-primary-xiketic'}>All metrics</h4>
            <div className={'flex items-center gap-2'}>
              <ToggleInput
                disabled={disabledInputs}
                name={'metrics_all'}
                label={'All'}
                trackClassName={'bg-primary-persian-blue/10'}
                onChange={(value) => {
                  if (value) metrics.forEach((metric) => setValue(metric.value, true))
                  else metrics.forEach((metric) => setValue(metric.value, false))
                }}
              />
            </div>
          </div>
          <div className={'grid grid-cols-3 gap-2 mt-4'}>
            {metrics.map((metric) => (
              <div
                key={metric.value}
                className={
                  'flex flex-row gap-3 justify-between items-center border-2 p-3 rounded-lg border-primary-persian-blue/10 has-[:checked]:bg-primary-persian-blue/20 bg-white'
                }>
                <ToggleInput
                  disabled={disabledInputs}
                  trackClassName={'bg-primary-persian-blue/10'}
                  label={metric.label}
                  onChange={(value) => {
                    if (value && checkAllMetricsToggled()) setValue('metrics_all', true)
                    else setValue('metrics_all', false)
                  }}
                  name={metric.value}
                />
              </div>
            ))}
          </div>
          <div className="flex flex-col w-full mt-6">
            <div className={'flex justify-between items-center w-full'}>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <h4 className={'font-bold text-base text-primary-xiketic'}>
                    All Conversions
                  </h4>
                  <Info
                    message={
                      'Contact your LinkOne rep to know which are available to you'
                    }
                  />
                </div>
                <p className="text-xs">
                  Your chosen attribution tracking is based on{' '}
                  {dealership?.attribution_tracking?.type?.replace('_', ' ') ||
                    'conversion'}{' '}
                  date.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4">
              {conversions?.map((conversion) => (
                <div
                  key={conversion.value}
                  className={
                    'flex flex-row gap-3 justify-between items-center border-2 p-3 rounded-lg border-primary-persian-blue/10 has-[:checked]:bg-primary-persian-blue/20 bg-white'
                  }>
                  <ToggleInput
                    disabled={disabledInputs}
                    trackClassName={'bg-primary-persian-blue/10'}
                    label={conversion.label}
                    name={conversion.value}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className={'flex justify-between items-center mt-4'}>
            <Button
              animate={false}
              type={'button'}
              variant={'tertiary'}
              onClick={(e) => {
                e.preventDefault()
                metrics.forEach((metric) => setValue(metric.value, false))
                setValue('metrics_all', false)
              }}>
              Clear Selection
            </Button>
            <Button
              animate={false}
              type={'button'}
              variant={'rainbow'}
              onClick={(e) => {
                e.preventDefault()
                setIsOpen(false)
              }}>
              Confirm Selection
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
